/**
 * AgentHome — "New Objective" landing for the omnipresent agent.
 * Renders the "State your objective" prompt (input with an in-line workflow
 * selector + attach + send, quick-action pills) and a "Recent programs" grid.
 * Sending a message transitions into the full-page agent chat. See DEV-407.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Paperclip,
  X,
  Sparkles,
  ArrowUp,
  ArrowRight,
} from 'lucide-react';
import { Button, Spinner } from '../components/ui';
import { useAgent } from '../contexts/AgentContext';
import { useAgentChat, ViewSuggestion } from '../hooks/useAgentChat';
import { useNavigateToView } from '../hooks/useNavigateToView';
import { programsAPI, type Program } from '../lib/services';
import { workflowsApi, type WorkflowSummary } from '../services/workflows';
import AgentChatView from '../components/AgentChatView';

/** Status filters for the "Recent programs" grid (mirrors the Programs list page). */
const PROGRAM_FILTERS: { label: string; value: 'all' | Program['status'] }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Planning', value: 'planning' },
  { label: 'Evaluation', value: 'evaluation' },
  { label: 'Closed', value: 'closed' },
];

/** How many programs to show in the grid. */
const PROGRAM_LIMIT = 6;

/** Rough progress fraction per program status, for the card phase ring. */
const STATUS_PROGRESS: Record<Program['status'], number> = {
  planning: 0.12,
  active: 0.35,
  evaluation: 0.55,
  field_test: 0.72,
  awarded: 0.9,
  closed: 1,
};

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

/** Small circular progress "tracker" mark for a program card. */
function PhaseRing({ status }: { status: Program['status'] }) {
  const frac = STATUS_PROGRESS[status] ?? 0.25;
  const r = 6.5;
  const c = 2 * Math.PI * r;
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" className="shrink-0 text-foreground" aria-hidden>
      <circle cx="8" cy="8" r={r} fill="none" stroke="currentColor" strokeOpacity={0.15} strokeWidth={2} />
      <circle
        cx="8"
        cy="8"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - frac)}
        transform="rotate(-90 8 8)"
      />
    </svg>
  );
}

const AgentHome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pageContext, conversationId, setConversationId, openDrawer } = useAgent();
  const navigateToView = useNavigateToView();
  // An agent auto-navigation (e.g. start_workflow → first workflow step) that
  // arrived mid-stream. Held until the turn completes: leaving /home unmounts
  // this page and closes its stream, and the drawer that picks the
  // conversation up hydrates from what the turn has persisted.
  const pendingNavRef = useRef<ViewSuggestion | null>(null);
  const [input, setInput] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [startingWorkflow, setStartingWorkflow] = useState<string | null>(null);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | Program['status']>('all');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    messages,
    isStreaming,
    viewSuggestions,
    actionSuggestions,
    pendingJobs,
    uploads,
    sendMessage,
    setUploadProgramContext,
    undo,
    dismissSuggestion,
    dismissActionSuggestion,
  } = useAgentChat({
    conversationId: conversationId,
    onViewSuggestion: (suggestion: ViewSuggestion) => {
      pendingNavRef.current = suggestion;
    },
    onError: (error: string) => {
      console.error('Agent error:', error);
    },
    onConversationCreated: (newConversationId: string) => {
      setConversationId(newConversationId);
      setShowChat(true);
    },
  });

  // Leaving /home for an agent surface: the conversation continues in the
  // drawer. conversationId lives in AgentContext, so it survives this page
  // unmounting (the reset below only runs on arrival at /home).
  const continueInDrawer = (suggestion: ViewSuggestion) => {
    if (navigateToView(suggestion)) openDrawer();
  };

  useEffect(() => {
    if (isStreaming || !pendingNavRef.current) return;
    const suggestion = pendingNavRef.current;
    pendingNavRef.current = null;
    continueInDrawer(suggestion);
  }, [isStreaming]);

  // Reset to landing page whenever the user navigates to /home
  useEffect(() => {
    setConversationId(null);
    setShowChat(false);
  }, [location.pathname, setConversationId]);

  // Fetch active programs, most recently updated first
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoadingPrograms(true);
        const all = await programsAPI.list();
        const sorted = [...all].sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
        setPrograms(sorted);
      } catch (error) {
        console.error('Error fetching programs:', error);
      } finally {
        setLoadingPrograms(false);
      }
    };

    fetchPrograms();
  }, []);

  // Composable workflows for the bubbles above the chat.
  useEffect(() => {
    workflowsApi
      .listWorkflows()
      .then((r) => setWorkflows(r.workflows))
      .catch((e) => console.error('Error fetching workflows:', e));
  }, []);

  // Clicking a workflow creates a program on it and launches straight into its
  // first step (e.g. Basic Requirements) — not the agent chat.
  const handleStartWorkflow = async (wf: WorkflowSummary) => {
    if (startingWorkflow) return;
    setStartingWorkflow(wf.key);
    try {
      const program = await programsAPI.create({
        name: 'Untitled program',
        description: `Started from “${wf.name}”.`,
        workflow: wf.key,
      } as any);
      const dest = wf.first_step_key
        ? `/programs/${program.id}/module/${wf.first_step_key}`
        : `/programs/${program.id}`;
      navigate(dest);
    } catch (e) {
      console.error('Failed to start workflow:', e);
      setStartingWorkflow(null);
    }
  };

  // Programs shown in the grid: filtered by status, capped to PROGRAM_LIMIT.
  const visiblePrograms = useMemo(() => {
    const filtered =
      statusFilter === 'all' ? programs : programs.filter((p) => p.status === statusFilter);
    return filtered.slice(0, PROGRAM_LIMIT);
  }, [programs, statusFilter]);

  const handleLandingSend = () => {
    if (!input.trim() || isStreaming) return;

    setShowChat(true);

    const filesToSend = pendingFiles.length > 0 ? [...pendingFiles] : undefined;
    if (filesToSend) {
      setPendingFiles([]);
    }

    // Chat launches the full-page agent with NO workflow (workflows are the
    // bubbles above the input now).
    sendMessage(input, pageContext, undefined, filesToSend);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleLandingSend();
    }
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      setPendingFiles((prev) => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setPendingFiles((prev) => [...prev, ...files]);
    }
  };

  const handleUndo = async (editId: string) => {
    try {
      await undo(editId);
    } catch (error) {
      console.error('Failed to undo:', error);
    }
  };

  const handleChatSend = (text: string, files?: File[]) => {
    sendMessage(text, pageContext, undefined, files);
  };

  // If showing chat, render shared AgentChatView (full variant)
  if (showChat) {
    return (
      <div className="h-full flex flex-col bg-background relative">
        <AgentChatView
          variant="full"
          messages={messages}
          isStreaming={isStreaming}
          viewSuggestions={viewSuggestions}
          actionSuggestions={actionSuggestions}
          pendingJobs={pendingJobs}
          uploads={uploads}
          onToggleUploadContext={setUploadProgramContext}
          onSend={handleChatSend}
          onUndo={handleUndo}
          onDismissSuggestion={dismissSuggestion}
          onDismissActionSuggestion={dismissActionSuggestion}
          onNavigateToView={continueInDrawer}
          emptyState={
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-lg text-muted-foreground">Starting conversation...</p>
            </div>
          }
        />
      </div>
    );
  }

  // Landing view — "State your objective"
  return (
    <div
      className="h-full overflow-auto bg-background relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 z-50 m-4 flex items-center justify-center rounded-xl border-2 border-dashed border-border bg-accent/40 pointer-events-none">
          <div className="text-lg font-medium text-foreground">Drop files here to attach</div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFilePick}
      />

      <div className="mx-auto w-full max-w-3xl px-6 py-16 sm:py-24">
        {/* Objective prompt */}
        <section className="flex flex-col gap-6">
          <h1 className="text-2xl font-medium text-foreground text-center">What do you want to do?</h1>

          {/* Workflow bubbles — start a guided workflow, launching into its first step. */}
          {workflows.length > 0 && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {workflows.map((wf) => (
                <button
                  key={wf.key}
                  type="button"
                  onClick={() => handleStartWorkflow(wf)}
                  disabled={!!startingWorkflow}
                  className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-accent disabled:opacity-60"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">{wf.name}</div>
                    <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{wf.description}</div>
                  </div>
                  {startingWorkflow === wf.key ? (
                    <Spinner size="sm" />
                  ) : (
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or just ask <div className="h-px flex-1 bg-border" />
          </div>

          {/* Input */}
          <div className="rounded-xl border border-border bg-card px-6 pt-6 pb-4 shadow-xs">
            {pendingFiles.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {pendingFiles.map((file, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1.5 text-xs text-muted-foreground"
                  >
                    <Paperclip className="h-3 w-3" />
                    <span className="max-w-[150px] truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removePendingFile(i)}
                      className="ml-0.5 transition-colors hover:text-foreground"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Start a new acquisition program, market search, and more..."
              rows={1}
              className="block w-full resize-none overflow-hidden border-0 bg-transparent text-base text-foreground placeholder:text-muted-foreground outline-none focus:outline-none focus-visible:outline-none focus:ring-0"
              style={{ minHeight: '56px', maxHeight: '240px' }}
              autoFocus
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 240) + 'px';
              }}
            />

            <div className="mt-6 flex items-end justify-between gap-2">
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  className="h-9 w-9 rounded-full text-muted-foreground"
                  aria-label="Attach files"
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
              </div>
              <Button
                type="button"
                size="icon"
                onClick={handleLandingSend}
                disabled={!input.trim()}
                className="h-10 w-10 rounded-full"
                aria-label="Send"
              >
                <ArrowUp className="h-5 w-5" />
              </Button>
            </div>
          </div>

        </section>

        {/* Active programs */}
        <section className="mt-20 flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-medium text-foreground">Recent programs</h2>
            <p className="max-w-xl text-xs text-muted-foreground">
              Programs you've worked on recently. Jump back in to refine requirements, search the
              market, or manage a solicitation.
            </p>
          </div>

          {!loadingPrograms && programs.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {PROGRAM_FILTERS.map((filter) => (
                <Button
                  key={filter.value}
                  type="button"
                  size="sm"
                  variant={statusFilter === filter.value ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(filter.value)}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          )}

          {loadingPrograms ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : programs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Your programs will appear here. Start one above to get going.
            </div>
          ) : visiblePrograms.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No programs match this filter.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {visiblePrograms.map((program) => (
                <button
                  key={program.id}
                  type="button"
                  onClick={() => navigate(`/programs/${program.id}`)}
                  className="flex min-h-[118px] flex-col justify-between rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-accent"
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <PhaseRing status={program.status} />
                      <span className="text-xs text-foreground/80">{program.status_display}</span>
                    </div>
                    <p className="line-clamp-2 text-sm font-medium text-foreground">{program.name}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Last update {timeAgo(program.updated_at)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AgentHome;
